import { useMemo } from "react"
import { partition, groupBy, keyBy, isEmpty } from "lodash"
import { orderMetricMetadata } from "helpers/metrics/utils"

/**
 * Custom hook for reordering metricMetadata based on column order mode
 *
 * @param {Array} metricMetadata - Original metric metadata array
 * @param {string} columnOrder - Column order mode ("default", "custom", or magnitude-based)
 * @param {Object} data - Current level data containing values for magnitude ordering
 * @param {Array} savedCustomColumnOrder - Saved custom column order array (independent of other DataGrid state)
 * @returns {Array} Reordered metric metadata array
 */
export const useOrderedMetricMetadata = (metricMetadata, columnOrder, data, savedCustomColumnOrder) => {
  return useMemo(() => {
    // Handle custom ordering - use saved custom column order from persistence
    if (columnOrder === "custom") {
      if (isEmpty(savedCustomColumnOrder) || isEmpty(metricMetadata)) {
        return metricMetadata
      }

      const [metricsInSavedOrder, remainingMetrics] = partition(metricMetadata, metric =>
        savedCustomColumnOrder.includes(metric.id),
      )
      const remainingMetricsByPrefix = groupBy(remainingMetrics, "prefix")
      const savedMetricsById = keyBy(metricsInSavedOrder, "id")

      // Combine saved metrics with their related groups in the correct order
      const orderedMetrics = savedCustomColumnOrder.flatMap(columnId => {
        const savedMetric = savedMetricsById[columnId]
        const metricPrefix = savedMetric?.prefix
        return [
          ...(savedMetric ? [savedMetric] : []),
          ...(metricPrefix ? remainingMetricsByPrefix[metricPrefix] || [] : []),
        ]
      })

      // Find metrics that weren't included by prefix matching and add them to the end
      const includedRemainingMetrics = new Set(
        savedCustomColumnOrder.flatMap(columnId => {
          const savedMetric = savedMetricsById[columnId]
          const metricPrefix = savedMetric?.prefix
          return metricPrefix ? remainingMetricsByPrefix[metricPrefix] || [] : []
        }),
      )
      const unmatchedMetrics = remainingMetrics.filter(metric => !includedRemainingMetrics.has(metric))

      return [...orderedMetrics, ...unmatchedMetrics]
    }

    return orderMetricMetadata(metricMetadata, columnOrder, data?.values)
  }, [metricMetadata, columnOrder, data?.values, savedCustomColumnOrder])
}
